import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { adminApi } from "../../lib/api";
import { useSettings } from "../../lib/settings";
import AdminLayout from "../../components/app/AdminLayout";
import { StatusBadge } from "../../components/app/StatusBadge";
import { Plus, ArrowRight, Loader2 } from "lucide-react";
import {
  LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, AreaChart, Area,
  PieChart, Pie, Cell, BarChart, Bar, CartesianGrid,
} from "recharts";

export default function Dashboard() {
  const { formatDate, settings } = useSettings();
  const [stats, setStats] = useState(null);
  const [statuses, setStatuses] = useState([]);
  const [declines, setDeclines] = useState([]);

  useEffect(() => {
    adminApi.get("/admin/stats").then(({ data }) => setStats(data));
    adminApi.get("/admin/statuses").then(({ data }) => setStatuses(data.items));
    adminApi.get("/admin/role-declines").then(({ data }) => setDeclines(data.items || []));
  }, []);

  return (
    <AdminLayout
      title="Dashboard"
      subtitle="Overview of your scare actor recruitment"
      actions={
        <Link to="/" target="_blank" className="flex items-center gap-2 px-4 py-2 text-xs uppercase tracking-wider bg-blood text-white hover:bg-[#FF3333]" data-testid="new-application-btn">
          <Plus size={14} /> Public Form
        </Link>
      }
    >
      {!stats ? <Loader/> : (
        <div className="space-y-6">
          {/* Role declined notifications (Update 20) */}
          {declines.length > 0 && (
            <div className="space-y-2" data-testid="dashboard-declines">
              {declines.map((d) => (
                <Link key={d.id} to={`/admin/applicants/${d.actor_id}`} data-testid={`decline-notice-${d.actor_id}`}
                  className="flex items-center justify-between gap-3 px-4 py-3 bg-blood/10 border border-blood/40 hover:bg-blood/15 transition-colors">
                  <span className="text-sm text-[#F2EBE1]">
                    <span className="text-blood font-medium">{d.actor_name || "An actor"}</span> has declined their role offer{d.role_name ? ` for ${d.role_name}` : ""}.
                    {d.reason ? <span className="text-[#A19D94] italic"> — "{d.reason}"</span> : null}
                  </span>
                  <span className="text-[11px] uppercase tracking-wider text-[#A19D94] shrink-0 flex items-center gap-1">Review <ArrowRight size={12} /></span>
                </Link>
              ))}
            </div>
          )}

          {/* KPI cards */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <StatCard label="Total Applications" value={stats.total_applications} delta={`+${stats.new_this_week} this week`} testid="stat-total" />
            <StatCard label="New This Week" value={stats.new_this_week} delta="last 7 days" testid="stat-new-week" />
            <StatCard label="Audition Invites" value={stats.audition_invites} delta="awaiting response" testid="stat-auditions" />
            <StatCard label="Accepted Actors" value={stats.accepted_actors} delta={`${stats.contracts_signed} signed`} testid="stat-accepted" />
            <StatCard label="Contracts Signed" value={stats.contracts_signed} delta="cast confirmed" testid="stat-contracts" />
          </div>

          {/* Charts row 1 */}
          <div className="grid lg:grid-cols-3 gap-3">
            <Card title="Applications Over Time" className="lg:col-span-2">
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={stats.over_time}>
                    <defs>
                      <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#D91616" stopOpacity={0.4} />
                        <stop offset="100%" stopColor="#D91616" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#1a1a1a" vertical={false} />
                    <XAxis dataKey="date" stroke="#6B6862" fontSize={10} tickFormatter={(d)=>formatDate(d, "medium").slice(0, 6)} />
                    <YAxis stroke="#6B6862" fontSize={10} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #262626", fontSize: 12 }} labelStyle={{ color: "#A19D94" }} />
                    <Area type="monotone" dataKey="count" stroke="#D91616" strokeWidth={2} fill="url(#g1)" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card title="Gender Breakdown">
              <div className="h-64 flex items-center">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={stats.gender_breakdown} dataKey="count" nameKey="gender" innerRadius={50} outerRadius={80} paddingAngle={2}>
                      {stats.gender_breakdown.map((_, i) => <Cell key={i} fill={["#D91616","#8B5CF6","#F59E0B","#84CC16","#6B6862"][i%5]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #262626", fontSize: 12 }} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div className="space-y-1.5 mt-2">
                {stats.gender_breakdown.map((g, i) => (
                  <div key={g.gender} className="flex items-center justify-between text-xs">
                    <span className="flex items-center gap-2 text-[#A19D94]"><span className="w-2 h-2 rounded-full" style={{ background: ["#D91616","#8B5CF6","#F59E0B","#84CC16","#6B6862"][i%5] }} />{g.gender}</span>
                    <span className="font-mono">{g.count}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {/* Charts row 2 */}
          <div className="grid lg:grid-cols-2 gap-3">
            <Card title="Applications by Status">
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart layout="vertical" data={stats.by_status} margin={{ left: 20, right: 20 }}>
                    <XAxis type="number" stroke="#6B6862" fontSize={10} allowDecimals={false} />
                    <YAxis type="category" dataKey="label" stroke="#6B6862" fontSize={10} width={110} />
                    <Tooltip contentStyle={{ background: "#0A0A0A", border: "1px solid #262626", fontSize: 12 }} />
                    <Bar dataKey="count">
                      {stats.by_status.map((s, i) => <Cell key={i} fill={s.color === "#FFFFFF" ? "#A19D94" : s.color} />)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </Card>

            <Card title="Recent Applications" action={<Link to="/admin/applicants" className="text-xs text-blood hover:underline tracking-wider uppercase">View All →</Link>}>
              <div className="divide-y divide-[#1a1a1a]">
                {stats.recent_applications.slice(0,8).map((a) => (
                  <Link key={a.id} to={`/admin/applicants/${a.id}`} className="flex items-center justify-between py-2.5 hover:bg-[#101010] -mx-3 px-3 transition-colors">
                    <div className="min-w-0">
                      <div className="text-sm truncate">{a.full_name}</div>
                      <div className="font-mono text-[10px] text-[#6B6862]">{formatDate(a.submitted_at)}</div>
                    </div>
                    <StatusBadge status={a.status} statuses={statuses} />
                  </Link>
                ))}
                {stats.recent_applications.length === 0 && <p className="py-6 text-center text-sm text-[#6B6862]">No applications yet.</p>}
              </div>
            </Card>
          </div>

          {/* Upcoming auditions */}
          {stats.upcoming_auditions.length > 0 && (
            <Card title="Upcoming Auditions" action={<Link to="/admin/auditions" className="text-xs text-blood hover:underline tracking-wider uppercase">Manage →</Link>}>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
                {stats.upcoming_auditions.slice(0,6).map((au) => (
                  <div key={au.id} className="border border-[#1a1a1a] bg-[#050505] p-4">
                    <div className="font-mono text-[10px] text-[#6B6862] uppercase">{formatDate(au.date, "weekday-day")}</div>
                    <div className="font-display text-lg uppercase mt-1">{au.name}</div>
                    <div className="text-xs text-[#A19D94] mt-1">{au.location}</div>
                    <div className="mt-3 flex items-center justify-between text-xs">
                      <span className="text-[#A19D94]">{au.booked_count}{au.capacity ? `/${au.capacity}` : ""} booked</span>
                      <ArrowRight size={12} className="text-blood" />
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}
    </AdminLayout>
  );
}

export const Card = ({ title, children, className = "", action }) => (
  <div className={`border border-[#1a1a1a] bg-[#0A0A0A] p-5 ${className}`}>
    <div className="flex items-center justify-between mb-4">
      <h3 className="label-mini">{title}</h3>
      {action}
    </div>
    {children}
  </div>
);

const StatCard = ({ label, value, delta, testid }) => (
  <div className="border border-[#1a1a1a] bg-[#0A0A0A] p-4" data-testid={testid}>
    <div className="label-mini">{label}</div>
    <div className="font-display text-3xl font-bold mt-2 tracking-tight">{value}</div>
    <div className="text-[10px] text-[#6B6862] mt-1 font-mono uppercase tracking-wider">{delta}</div>
  </div>
);

export const Loader = () => (
  <div className="flex items-center justify-center py-20"><Loader2 className="animate-spin text-blood" /></div>
);
